import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { I3PastEventMedia } from '../cms/types';
import { resolvePublicMediaSrc } from '../utils/i3Assets';

const ROTATE_MS = 5500;

type Props = {
  photos: I3PastEventMedia[];
  onOpen: (src: string, title?: string) => void;
  className?: string;
};

const I3EventsPhotoRotator: React.FC<Props> = ({ photos, onOpen, className }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const list = photos.filter((p) => p.type === 'image' && p.src);
  const len = list.length;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const fn = () => setReducedMotion(mq.matches);
    mq.addEventListener?.('change', fn);
    return () => mq.removeEventListener?.('change', fn);
  }, []);

  const go = useCallback(
    (dir: -1 | 1) => {
      if (len === 0) return;
      setIndex((i) => (i + dir + len) % len);
    },
    [len],
  );

  useEffect(() => {
    if (len <= 1 || paused || reducedMotion) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      return;
    }
    timerRef.current = setInterval(() => go(1), ROTATE_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [len, paused, reducedMotion, go]);

  if (len === 0) return null;

  const current = list[index];
  const imgSrc = resolvePublicMediaSrc(current.src);

  return (
    <div
      className={`i3-events-photo-rotator ${className ?? ''}`}
      role="region"
      aria-label="Photo highlights"
    >
      <button
        type="button"
        className="i3-events-photo-rotator-frame"
        onClick={() => onOpen(current.src, current.title)}
        aria-label={current.title || 'View photo fullscreen'}
      >
        <img src={imgSrc} alt={current.title || ''} className="i3-events-photo-rotator-img" />
      </button>
      {len > 1 ? (
        <>
          <div className="i3-events-photo-rotator-controls">
            <button type="button" className="i3-events-photo-rotator-btn" onClick={() => go(-1)} aria-label="Previous photo">
              ‹
            </button>
            <button type="button" className="i3-events-photo-rotator-btn" onClick={() => go(1)} aria-label="Next photo">
              ›
            </button>
          </div>
          <div className="i3-events-photo-rotator-dots">
            {list.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`i3-events-photo-rotator-dot${i === index ? ' i3-events-photo-rotator-dot--active' : ''}`}
                onClick={() => setIndex(i)}
                aria-label={`Photo ${i + 1} of ${len}`}
                aria-current={i === index}
              />
            ))}
          </div>
          <button
            type="button"
            className="i3-events-photo-rotator-pause"
            onClick={() => setPaused((p) => !p)}
            aria-pressed={paused}
          >
            {paused || reducedMotion ? 'Play' : 'Pause'}
          </button>
        </>
      ) : null}
    </div>
  );
};

export default I3EventsPhotoRotator;
